import { Box, Button, Grid, useTheme } from '@mui/material';
import { useAtom } from 'ximple';
import { useLocale } from '@repo/i18n';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { cartAtom } from 'src/state/cart/cartAtom';
import { nativeMemberCoverageAtom } from 'src/state/native-membership/nativeMemberCoverageAtom';
import useCartItemsForSummary from 'src/hooks/domain/useCartItemsForSummary';
import NativeMemberCardSelectionPanel from 'src/widgets/native-membership/NativeMemberCardSelectionPanel';
import { useCartCheckoutContext } from '../CartCheckoutContext';
import CheckoutSummary, { useSummaryFeatures } from '../../CheckoutSummary';
import { ButtonDisplay } from './ButtonDisplay';

type MemberCardPaymentViewProps = {
    handleGoPayClicked: () => void;
    handleGoPayClickedInvoice?: () => void;
};

// Native "Velg betalingsmetode" step: the member steers which member cards cover which lines on the
// left, the summary updates live on the right (both read the shared coverage atom). Modelled on
// IntentView — selections left, CheckoutSummary right; reserve runs from this step's go-pay.
export function MemberCardPaymentView({
    handleGoPayClicked,
    handleGoPayClickedInvoice,
}: MemberCardPaymentViewProps) {
    const { t } = useLocale();
    const theme = useTheme();
    const customizations = useCustomizations();
    const [cartItems] = useAtom(cartAtom);
    // Mirrors CheckoutSummary's enableGoPay gate: while a /calculate is in flight or failed the
    // member must not reserve against a stale total or an un-applied discount. (This view only
    // renders for native members, so no membership-mode check is needed.)
    const [{ isCalculating: isCalculatingCoverage, error: coverageError }] =
        useAtom(nativeMemberCoverageAtom);
    const { activeTab, largeScreen, isMakingReservation } = useCartCheckoutContext();

    const cartItemsForSummary = useCartItemsForSummary();
    const summaryFeatures = useSummaryFeatures(
        true,
        true,
        true,
        activeTab,
        cartItemsForSummary.isWaitingListMode,
    );

    return (
        <Grid
            container
            justifyContent={largeScreen ? 'space-between' : 'center'}
            alignItems={largeScreen ? 'flex-start' : 'center'}
            direction={largeScreen ? 'row' : 'column-reverse'}
        >
            {!largeScreen && (
                <Box
                    maxWidth="440px"
                    width="100%"
                    sx={{
                        [theme.breakpoints.down('md')]: { marginBottom: theme.spacing(3) },
                    }}
                >
                    <Button
                        sx={{ float: 'right', marginTop: theme.spacing(2) }}
                        color="primary"
                        variant={customizations.primaryButtonStyle}
                        disabled={
                            isMakingReservation ||
                            cartItems.length === 0 ||
                            isCalculatingCoverage ||
                            coverageError
                        }
                        onClick={handleGoPayClicked}
                    >
                        <ButtonDisplay
                            isMakingReservation={isMakingReservation}
                            buttonText={t.go_and_pay}
                        />
                    </Button>
                </Box>
            )}

            <NativeMemberCardSelectionPanel />

            <CheckoutSummary
                cartItems={cartItemsForSummary.itemsToShow}
                hideGoPay={!largeScreen}
                isMakingReservation={isMakingReservation}
                goPayClicked={handleGoPayClicked}
                goPayClickedInvoice={handleGoPayClickedInvoice}
                summaryFeatures={summaryFeatures}
                nextButtonText={t.go_and_pay}
            />
        </Grid>
    );
}
